import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { useConfig } from 'hooks/use-config';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useLanguageContext } from 'i18n/language-context';
import { updateCurrentLanguage } from 'components/blocks/language-selector/services/language-service';
import { useGetAccount, useUpdateAccount } from 'features/profile/hooks/use-account';
import { ProfilePictureCard } from './profile-picture-card';
import { ProfileDetailsCard, type ProfileDetailsChanges } from './profile-details-card';
import { WorkspaceCard } from './workspace-card';
import { DeleteAccountCard } from './delete-account-card';
import { toAccountUpdate, type AccountChanges } from './account-update-payload';

// The API keeps first and last name: the first word is the first name, the rest is the last name.
const splitFullName = (fullName: string) => {
  const spaceIndex = fullName.indexOf(' ');
  if (spaceIndex === -1) return { firstName: fullName, lastName: '' };
  return {
    firstName: fullName.slice(0, spaceIndex),
    lastName: fullName.slice(spaceIndex + 1).trim(),
  };
};

/** Profile tab: loads and saves the signed-in account with the same APIs as the Settings dialog. */
export function AccountProfileTab() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { configurationSettings } = useConfig();
  const { setLanguage } = useLanguageContext();
  const user = useCommonStore((state) => state.user);
  const setUser = useCommonStore((state) => state.setUser);
  const { data: account, isLoading, isError } = useGetAccount({ enabled: true });
  const { mutate: updateAccount, isPending: isSaving } = useUpdateAccount();
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';

  // Keep the header avatar and name in step with the saved account, like the Settings dialog does.
  useEffect(() => {
    if (account) setUser(account);
  }, [account, setUser]);

  if (isLoading) {
    return (
      <div data-testid="account-profile-loading" className="flex flex-col gap-4">
        <Skeleton className="h-28 w-full rounded-section" />
        <Skeleton className="h-64 w-full rounded-section" />
        <Skeleton className="h-36 w-full rounded-section" />
      </div>
    );
  }

  if (isError || !account) {
    return <p className={cn('py-6 text-sm', mutedColor)}>{t('SOMETHING_WENT_WRONG')}</p>;
  }

  const saveProfile = (changes: AccountChanges) => {
    updateAccount(toAccountUpdate(account, changes));
  };

  const fullName = `${account.firstName ?? ''} ${account.lastName ?? ''}`.trim();

  const handleDetailsSave = async ({ fullName: newName, language }: ProfileDetailsChanges) => {
    if (newName) saveProfile(splitFullName(newName));
    if (language) {
      // Same as the language switch in the profile menu.
      if (user) updateCurrentLanguage(user.id, language, configurationSettings);
      await setLanguage(language);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ProfilePictureCard
        imageUrl={account.profileImageUrl}
        isSaving={isSaving}
        onChange={(profileImageUrl) => saveProfile({ profileImageUrl })}
      />
      <ProfileDetailsCard
        fullName={fullName}
        email={account.email}
        isVerified={account.isVerified}
        isSaving={isSaving}
        onSave={handleDetailsSave}
      />
      <WorkspaceCard memberSince={account.createdDate} />
      <DeleteAccountCard />
    </div>
  );
}
